import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { Title, TitleProps } from "./Title";
import { SimpleRichText, SimpleRichTextProps } from "./SimpleRichText";
import { Button, ButtonProps } from "./Button";

export interface SectionHeaderProps
  extends Omit<ComponentProps<"div">, "title"> {
  title?: TitleProps | null;
  text?: SimpleRichTextProps | null;
  buttons?: ButtonProps[] | null;
  layout?: "center" | "left" | "split";
  variant?: "default" | "location";
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  text,
  buttons,
  layout = "left",
  variant = "default",
  className,
  ...props
}) => {
  const hasContent = title || text || buttons;
  const gapClass =
    variant === "location"
      ? "uipm:gap-[var(--padding-padding-xs)]"
      : "uipm:gap-[var(--margin-margin-md)]";
  const desktopGapClass =
    variant === "location"
      ? "uipm:md:gap-[var(--padding-padding-xs)]"
      : "uipm:md:gap-[var(--padding-padding-default)]";

  if (!hasContent) {
    return null;
  }

  if (layout === "center") {
    return (
      <div className={cn(className)} {...props}>
        <div className="uipm:md:p-0">
          <div
            className={cn(
              "uipm:flex uipm:flex-col uipm:items-center",
              gapClass,
              desktopGapClass,
            )}
          >
            <div
              className={cn(
                "uipm:flex uipm:flex-col uipm:items-center uipm:text-center",
                gapClass,
                desktopGapClass,
              )}
            >
              {title && <Title {...title} className={cn(title.className)} />}

              {text && (
                <SimpleRichText
                  {...text}
                  className={cn(
                    "uipm:text-[var(--typography-colors-text-faded-dark)]",
                    text.className,
                  )}
                />
              )}
            </div>

            {buttons && buttons.length > 0 && (
              <div className="uipm:flex uipm:flex-row uipm:gap-[var(--margin-margin-sm)]">
                {buttons.filter(Boolean).map((button, index) => (
                  <Button
                    key={index}
                    {...button}
                    className={cn(
                      "uipm:flex-[1_0_0] uipm:md:flex-initial",
                      button.className,
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === "split") {
    return (
      <div className={cn(className)} {...props}>
        <div className="uipm:md:p-0">
          <div
            className={cn(
              "uipm:grid",
              "uipm:grid-cols-1",
              "uipm:md:grid-cols-2",
              "uipm:md:gap-6",
              gapClass,
            )}
          >
            {title && (
              <div className="uipm:col-start-1 uipm:row-start-1">
                <Title {...title} className={cn(title.className)} />
              </div>
            )}

            {text && (
              <div className="uipm:col-start-1 uipm:row-start-2 uipm:md:col-start-2 uipm:md:row-start-1">
                <SimpleRichText
                  {...text}
                  className={cn(
                    "uipm:text-[var(--typography-colors-text-faded-dark)]",
                    text.className,
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default "left" layout
  return (
    <div className={cn(className)} {...props}>
      <div className="uipm:md:p-0">
        <div
          className={cn(
            "uipm:grid",
            "uipm:grid-cols-1",
            "uipm:md:grid-cols-[1fr_auto]",
            "uipm:md:justify-between",
            "uipm:md:items-start",
            gapClass,
          )}
        >
          <div
            className={cn(
              "uipm:flex uipm:flex-col",
              "uipm:col-start-1",
              "uipm:row-start-1",
              "uipm:md:max-w-[920px]",
              gapClass,
              desktopGapClass,
            )}
          >
            {title && <Title {...title} className={cn(title.className)} />}

            {text && (
              <SimpleRichText
                {...text}
                className={cn(
                  "uipm:text-[var(--typography-colors-text-faded-dark)]",
                  text.className,
                )}
              />
            )}
          </div>

          {buttons && buttons.length > 0 && (
            <div
              className={cn(
                "uipm:flex uipm:gap-[var(--margin-margin-sm)]",
                "uipm:col-start-1 uipm:row-start-2",
                "uipm:md:col-start-2 uipm:md:row-start-1",
                "uipm:md:flex-shrink-0",
              )}
            >
              {buttons.filter(Boolean).map((button, index) => (
                <Button
                  key={index}
                  {...button}
                  className={cn(
                    "uipm:flex-[1_0_0] uipm:md:flex-initial",
                    button.className,
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
